import {
  getTheme,
  postAccessCode,
  getSourceRegistry,
  getSourceRegistryPrivate,
  postStreamLicense,
  postOrganizerViewerToken,
  postChatMessage,
  deleteChatMessage,
  postReaction,
  postAdminMessage,
  getChatHistory,
  getPinnedMessage,
  postBanUser,
  postUnbanUser,
  postBanAccess,
  getBannedChatUsers,
  putUpdateStreamSettings,
  getBroadcasterSettings,
  getAnalytics,
  postRoom,
  getRoom,
  getAllRules,
  getHLS,
  getMuxEvents,
  postAddRule,
  deleteRule,
  patchConvertToVOD,
  getStreamAssets,
  getStreamAsset,
  postEnableMasterAccess,
  putCompleteStream,
} from './net.js';
import {
  errorState,
  accessState,
  eventState,
  streamState,
  hlsState,
  playerState,
  drmState,
  bannedState,
  bannedUsersState,
  themeState,
  chatState,
  orgUserState,
  broadcasterState,
  analyticsState,
  roomsState,
  rulesState,
  streamAssetsState,
  muxLogState,
} from './state.js';
import {
  ERROR_CRITICAL,
  PLAYER_VIEW_FULLSCREEN,
  PLAYER_VIEW_STANDARD,
  PLAYER_CMD_PLAY,
  PLAYER_CMD_PAUSE,
  PLAYER_CMD_PLAY_IF_NOT_PAUSED,
  PLAYER_CMD_JUMP_TO_LIVE,
  PLAYER_CMD_JUMP_TO_POSITION,
  PLAYER_CMD_SOUND_ON,
  PLAYER_CMD_SOUND_MUTED,
  PLAYER_CMD_SOUND_SET_LEVEL,
  PLAYER_CMD_FULLSCREEN,
  HTTP_ERROR_NOT_FOUND,
  HTTP_ERROR_UNAUTHORIZED,
  ERROR_WARNING,
  CHAT_GENERAL_CHANNEL_NAME,
  HLS_STATE_STREAM_INACTIVE,
  HLS_STATE_UNABLE_TO_FETCH_STREAM,
} from './constants.js';
import {
  ERROR_CODE_USER_ACCESS_INVALID,
  ERROR_CODE_USER_ACCESS_PRE_WINDOW,
  ERROR_CODE_USER_ACCESS_POST_WINDOW,
  ERROR_CODE_USER_BANNED,
  ERROR_CODE_USER_BANNED_LOCATION,
} from './errorcodes.js';
import {
  YOUR_MESSAGE_WAS_REJECTED,
  YOUR_MESSAGE_WAS_REJECTED_RATE_LIMIT,
  YOUR_REACTION_WAS_REJECTED_RATE_LIMIT,
  INVALID_ACCESS_CODE,
  ACCESS_CODE_PRE_USER_WINDOW,
  ACCESS_CODE_PRE_USER_WINDOW_WITH_TIME,
  ACCESS_CODE_POST_USER_WINDOW,
  ROOM_FAILED_TO_CREATE_ROOM,
  ROOM_DOES_NOT_EXIST,
  CHAT_FAILED_TO_POST_ADMIN_MESSAGE,
  CHAT_FAILED_TO_BAN_USER,
  CHAT_FAILED_TO_UNBAN_USER,
  CHAT_UNABLE_TO_CREATE_MODERATOR_CHAT_SESSION,
  CHAT_EMPTY_MESSAGE,
  USER_BANNED,
  FAILED_TO_BAN_USER_ACCESS,
  FAILED_TO_RETRIEVE_ANALYTICS,
  ACCESS_LOCATION_PROHIBITED,
} from './l10n/en_US.js';

// ACTIONS:
// Actions should only be retrieving and setting state. Do not put business logic in here.
// Logic should live in the relevant tags / components that subscribe (or really "react")
// to the changes in state.

// Error Reporting
export async function postError(error, type) {
  errorState.set({ error, type });
}

export async function clearError() {
  errorState.set({ error: '', type: '' });
}

export function sleep(delayTime) {
  return new Promise((resolve) => setTimeout(resolve, delayTime));
}

// Network Calls
export async function checkAccessCode(source, eventId, code, token, doCheckin) {
  try {
    const response = await postAccessCode(source, eventId, code, token, doCheckin);
    accessState.set({
      success: true,
      error: null,
      token: response.token,
      source,
      eventId,
      code,
      sub: response.subject,
      details: {},
    });
    drmState.setToken(response.token);
  } catch (e) {
    let err = INVALID_ACCESS_CODE;
    if (!!e.code) {
      if (e.code === ERROR_CODE_USER_ACCESS_PRE_WINDOW) {
        // Compose message base on existence of start
        // time in error response
        err = ACCESS_CODE_PRE_USER_WINDOW;
        if (e.details && e.details.start) {
          try {
            err = ACCESS_CODE_PRE_USER_WINDOW_WITH_TIME.replace(
              '{time}',
              moment(e.details.start).format('LL LTS')
            );
          } catch (e) {}
        }
      } else if (e.code === ERROR_CODE_USER_ACCESS_POST_WINDOW) {
        err = ACCESS_CODE_POST_USER_WINDOW;
      } else if (e.code === ERROR_CODE_USER_ACCESS_INVALID) {
        err = INVALID_ACCESS_CODE;
      } else if (e.code === ERROR_CODE_USER_BANNED) {
        err = USER_BANNED;
      } else if (e.code === ERROR_CODE_USER_BANNED_LOCATION) {
        err = ACCESS_LOCATION_PROHIBITED;
      }
    }
    accessState.set({
      success: false,
      error: err,
      token: null,
      source: '',
      eventId: '',
      sub: '',
    });
    drmState.setToken('');
    console.warn('[action] Access error', e);
    postError(err, ERROR_CRITICAL);
  }
}

export async function retrieveStreamInfo(source, eventId) {
  try {
    const stream = await getSourceRegistry(source, eventId);
    streamState.set(stream);
    eventState.set(stream);
  } catch (e) {
    console.warn('[action] Unable to get stream info', e);
    eventState.set404();
  }
}

export async function retrievePrivateStreamSettings(source, id) {
  try {
    const stream = await getSourceRegistryPrivate(source, id);
    streamState.set(stream);
    eventState.set(stream);
  } catch (e) {
    // distinguish 404 vs. other error
    if (e.code === HTTP_ERROR_NOT_FOUND) {
      eventState.set404();
      console.warn('[action] Event not found', e);
      return;
    }
    eventState.setError();
    console.warn('[action] Unable to get private stream info', e);
  }
}

export async function streamLicenseChallenge(token, challenge, position, playerStatus) {
  try {
    const resp = await postStreamLicense(token, challenge, position, playerStatus);
    drmState.setChallenge(resp.challenge);
    drmState.setValid(resp.valid);
  } catch (e) {
    if (e.code === HTTP_ERROR_UNAUTHORIZED) {
      bannedState.setBanned(true);
    } else {
      console.warn('[network] Post stream license challenge failed', e);
    }
  }
}

export async function checkHLS(hlsURL) {
  const res = await getHLS(hlsURL);
  if (res.status === 412) {
    hlsState.set(HLS_STATE_STREAM_INACTIVE);
  } else if (res.status >= 400) {
    hlsState.set(HLS_STATE_UNABLE_TO_FETCH_STREAM);
  } else {
    hlsState.set(null);
  }
}

export async function retrieveTheme(themeSlug, source, id) {
  try {
    themeState.setLoaded(false);
    themeState.setStyleSheet('/style/render/' + source + '/' + source + '/' + id);
  } catch (e) {
    console.warn('[network] Failed to get theme', e);
  } finally {
    setTimeout(() => {
      themeState.setLoaded(true);
    }, 1500);
  }
}

export async function joinRoom(source, eventId, roomId, token) {
  try {
    const room = await getRoom(source, eventId, roomId, token);
    roomsState.addRoom(room);
    roomsState.setCurrentRoom(roomId);
  } catch (e) {
    // show error that room didn't exist?
    postError(ROOM_DOES_NOT_EXIST, ERROR_WARNING);
    console.warn('[network] Failed to get room', e);
  }
}

export async function createRoom(source, eventId, roomId, name, token) {
  try {
    if (!name) return;
    const room = await postRoom(source, eventId, roomId, name, token);
    roomsState.addRoom(room); // automatically add / join a room we just created
    roomsState.setCurrentRoom(roomId);
  } catch (e) {
    postError(ROOM_FAILED_TO_CREATE_ROOM, ERROR_WARNING); // todo decipher non unique room id case
    console.warn('[network] Failed to get room:', e);
  }
}

export async function leaveRoom(source, eventID, roomId) {
  roomsState.removeRoom(roomId);
  roomsState.setCurrentRoom(null);
}

export async function retrieveOrganizerViewerToken(source, id) {
  try {
    const res = await postOrganizerViewerToken(source, id);
    orgUserState.setViewerToken(res.viewerToken);
  } catch (e) {
    console.warn(e);
    orgUserState.setError(CHAT_UNABLE_TO_CREATE_MODERATOR_CHAT_SESSION);
  }
}

export async function retrieveAnalytics(source, id, metric) {
  try {
    const res = await getAnalytics(source, id, metric);
    analyticsState.setMetric(metric, res);
  } catch (e) {
    console.warn(e);
    postError(FAILED_TO_RETRIEVE_ANALYTICS, ERROR_WARNING);
  }
}

export function setReceiver(receiver) {
  broadcasterState.setReceiver(receiver);
}

export async function sendAdminMessage(source, id, name, message, pin, time, hide_chat) {
  try {
    message = message.trim();
    if (!message) return;
    await postAdminMessage(source, id, name, message, pin, time, hide_chat);
  } catch (e) {
    console.warn(e);
    postError(CHAT_FAILED_TO_POST_ADMIN_MESSAGE, ERROR_WARNING);
  }
}

export async function banUser(source, id, subject, name) {
  try {
    await postBanUser(source, id, subject, name);
    bannedUsersState.addBannedChatUser(subject, name);
  } catch (e) {
    postError(CHAT_FAILED_TO_BAN_USER, ERROR_WARNING);
  }
}

export async function unbanUser(source, id, subject) {
  try {
    await postUnbanUser(source, id, subject);
    bannedUsersState.removeBannedChatUser(subject);
  } catch (e) {
    postError(CHAT_FAILED_TO_UNBAN_USER, ERROR_WARNING);
  }
}

export async function banAccess(source, id, subject, name) {
  try {
    await postBanAccess(source, id, subject, name);
  } catch (e) {
    postError(FAILED_TO_BAN_USER_ACCESS, ERROR_WARNING);
  }
}

export async function getBannedUsers(source, id) {
  try {
    const resp = await getBannedChatUsers(source, id);
    bannedUsersState.setBannedChatUsers(resp.banned_users);
  } catch (e) {
    console.warn('[network] Get banned chat users failed', e);
  }
}

export async function retrieveChatHistory(viewer_token, source, id, room, before) {
  try {
    room = room === CHAT_GENERAL_CHANNEL_NAME ? '' : room;
    let history = await getChatHistory(viewer_token, source, id, room, before);
    chatState.loadHistory(history.messages, room);
  } catch (e) {
    console.warn('[network] Failed to get chat history ', e);
  }
}

export async function retrievePinnedMessage(viewer_token, source, id) {
  try {
    const message = await getPinnedMessage(viewer_token, source, id);
    const time = new Date(Date.parse(message.time));
    chatState.pinMessage(message.id, message.message, time);
  } catch (e) {
    console.warn(
      '[network] Failed to get pinned messages - may be nothing pinned for this event',
      e
    );
  }
}

export async function sendChatMessage(viewer_token, source, id, name, message, room) {
  try {
    message = message.trim();
    if (!message) {
      setChatDefaultError(CHAT_EMPTY_MESSAGE, null);
      return;
    }
    room = room === CHAT_GENERAL_CHANNEL_NAME ? '' : room;
    await postChatMessage(viewer_token, source, id, name, message, room);
  } catch (e) {
    setChatDefaultError(message, e);
    console.warn('[network] Failed to post chat message ', e);
  }
}

export async function sendDeleteChatMessage(source, id, messageId) {
  try {
    if (!source || !id || !messageId) return;
    await deleteChatMessage(source, id, messageId);
  } catch (e) {
    setChatDefaultError(messageId, e);
    console.warn('[network] Failed to post chat message ', e);
  }
}

export async function sendReaction(viewer_token, source, id, reaction, position) {
  try {
    await postReaction(viewer_token, source, id, reaction, position);
  } catch (e) {
    // setChatError(messageId, e);
    console.warn('[network] Failed to post reaction ', e);
  }
}

export function setChatName(name) {
  chatState.setName(name);
}

// when room is null, we just listen on the default general channel
export function listenForChatMessages(room) {
  broadcasterState.addListener(room, 'chat.message', (data) => {
    chatState.appendMessage(data, room);
  });
}

// when room is null, we stop listening on the default general channel
export function stopListeningForChatMessages(room) {
  broadcasterState.removeListener(room, 'chat.message');
}

export function listenForChatDeleteMessages(room) {
  broadcasterState.addListener(room, 'chat.message.deletion', (data) => {
    chatState.deleteMessage(data, room);
  });
}

export function stopListeningForChatDeleteMessages(room) {
  broadcasterState.removeListener(room, 'chat.message.deletion');
}

export function listenForReactions() {
  broadcasterState.addListener(null, 'chat.reaction', (data) => {
    chatState.appendReaction(data);
  });
}

// when room is null, we stop listening on the default general channel
export function stopListeningForReactions() {
  broadcasterState.removeListener('chat.reaction');
}

export async function retrieveBroadcasterSettings(source, id) {
  try {
    // in the case of Pusher, settings = { app_id, app_key, cluster }
    const settings = await getBroadcasterSettings(source, id);
    broadcasterState.setSettings(settings);
  } catch (e) {
    console.warn('[action] Failed to retrieve broadcaster settings.', e);
  }
}

export async function updateStreamSettings(source, id, provider, updates) {
  try {
    const stream = await putUpdateStreamSettings(source, id, provider, updates);
    streamState.set(stream);
    eventState.set(stream);
  } catch (e) {
    console.warn('[action] Failed to update stream settings.', e);
  }
}

function _setChatError(displayMessage, consoleMessage, error) {
  errorState.set({ error: displayMessage, type: ERROR_WARNING });
  chatState.setError(
    "Your message was rejected due to either it's length, content, or the frequency of your posts."
  );
  console.warn('[action] Chat message failure: ' + consoleMessage, error);
}

export function setChatDefaultError(consoleMessage, error) {
  _setChatError(YOUR_MESSAGE_WAS_REJECTED, consoleMessage, error);
}

export function setChatRateLimitError() {
  _setChatError(YOUR_MESSAGE_WAS_REJECTED_RATE_LIMIT, YOUR_MESSAGE_WAS_REJECTED_RATE_LIMIT, null);
}

export function setReactionRateLimitError() {
  _setChatError(YOUR_REACTION_WAS_REJECTED_RATE_LIMIT, YOUR_REACTION_WAS_REJECTED_RATE_LIMIT, null);
}

export async function retrieveRules(source, eventId) {
  try {
    const rules = await getAllRules(source, eventId); // cookie auth
    rulesState.set(rules);
  } catch (e) {
    console.warn('[action] Failed to retrieve rule set.', e);
  }
}

export async function addRule(rule, token) {
  try {
    const newRule = await postAddRule(rule, token);
    rulesState.add(newRule);
  } catch (e) {
    console.warn('[action] Failed to add rule.', e);
    throw e;
  }
}

export async function removeRule(rule) {
  try {
    const rules = await deleteRule(rule);
    rulesState.remove(rule);
  } catch (e) {
    console.warn('[action] Failed to remove rule.', e);
  }
}

// Stream Player
// We intentionally keep state changes for the player as requests for state changes
// and actual state changes because the player itself
// ultimately is responsible for changing the state of the video element (i.e. the
// actual volume of the player vs. what our $state says the volume is). Binding directly to
// the variables via svelte is possible but ended up causing more problems than it solved.

// Stream Player Command Requests
// todo rename these all as "requestX"
export function clearCommandRequest() {
  playerState.setCommandRequest(null);
}
export function requestPlayVideo() {
  playerState.setCommandRequest(PLAYER_CMD_PLAY);
}
export function requestPlayVideoIfNotPaused() {
  playerState.setCommandRequest(PLAYER_CMD_PLAY_IF_NOT_PAUSED);
}
export function requestPauseVideo() {
  playerState.setCommandRequest(PLAYER_CMD_PAUSE);
}
export function requestJumpToLiveVideo() {
  playerState.setCommandRequest(PLAYER_CMD_JUMP_TO_LIVE);
}
export function requestJumpToPosition(percentage) {
  playerState.setCommandRequest(PLAYER_CMD_JUMP_TO_POSITION, percentage);
}
export function requestSoundOnVideo() {
  playerState.setCommandRequest(PLAYER_CMD_SOUND_ON);
}
export function requestSoundMutedVideo() {
  playerState.setCommandRequest(PLAYER_CMD_SOUND_MUTED);
}
export function requestSoundSetLevel(level) {
  playerState.setCommandRequest(PLAYER_CMD_SOUND_SET_LEVEL, level);
}
export function requestFullscreen() {
  playerState.setCommandRequest(PLAYER_CMD_FULLSCREEN);
}

// Stream Player State Changes
export function setSoundState(state) {
  playerState.setSoundState(state);
}
export function setPlayerState(state) {
  playerState.setState(state);
}
export function setPositionState(state) {
  playerState.setPosition(state);
}
export function setFullscreenStateFullscreen() {
  playerState.setView(PLAYER_VIEW_FULLSCREEN);
  // hack for now because we don't have a way to capture the 'exit' of full screen
  // could possible link it to key press of escape
  setTimeout(() => playerState.setView(PLAYER_VIEW_STANDARD), 500);
}

// Stream Player Misc.
export function showVideoControls() {
  playerState.setControls(true);
}
export function hideVideoControls() {
  playerState.setControls(false);
}

// convert
export async function convertToVOD(source, id, assetIndex, body) {
  try {
    const resp = await patchConvertToVOD(source, id, assetIndex, body);
  } catch (e) {
    console.warn('[network] Failed to convert to vod', e);
  }
}

export async function requestAssetDownload(source, id, asset) {
  try {
    await postEnableMasterAccess(source, id, asset);
  } catch (e) {
    console.warn('Mux asset not ready to be fetched.');
    throw e;
  }
}

export async function retrieveStreamAsset(source, id, assetID) {
  try {
    const response = await getStreamAsset(source, id, assetID);
    streamAssetsState.setAsset(assetID, response);
    return response;
  } catch (e) {
    console.warn('[network] Failed to get stream assets', e);
    throw e;
  }
}

export async function retrieveStreamAssets(source, id) {
  try {
    const response = await getStreamAssets(source, id);
    streamAssetsState.setAssets(response.assets);
  } catch (e) {
    console.warn('[network] Failed to get stream assets', e);
  }
}

export async function retrieveMuxEvents(source, id) {
  try {
    const events = await getMuxEvents(source, id);
    muxLogState.setLogs(events);
  } catch (e) {
    console.warn('[network] Failed to get mux events', e);
  }
}

// "end" stream
export async function endStream(source, id) {
  try {
    const stream = await putCompleteStream(source, id);
    streamState.set(stream);
    eventState.set(stream);
  } catch (e) {
    console.warn('[network] Failed to end stream', e);
  }
}
